/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, DoCheck, NgZone, OnInit, ViewChild} from '@angular/core';
import {CommonModule, NgFor, NgForOf, NgIf} from '@angular/common';
import {FormsModule, NgForm, ReactiveFormsModule} from '@angular/forms';
import {LookupService} from '../../../common/service/lookup.service';
import {MapService} from '../../services/map.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {MapToolsService} from '../../services/map-tools.service';
import {NgEventBus} from 'ng-event-bus';
import {filter, tap} from 'rxjs/operators';
import {NlrPermissionsDisabledPipe} from '../../../common/pipe/nlr-permissions-disabled-pipe';
import {WidgetService} from '../../services/widget.service';
import {MapModeService} from '../../services/map-mode.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {AnnotationToolComponent} from './annotation-tool/annotation-tool.component';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {PermissionsService} from '../../../common/service/permissions.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {auditTime} from 'rxjs';

@Component({
    selector: 'nlr-annotations-tools',
    imports: [
        CommonModule,
        FormsModule,
        NlrPermissionsDisabledPipe,
        AnnotationToolComponent,
        ReactiveFormsModule,
        CdkDrag,
        CdkDragHandle,
        HelpLinkComponent
    ],
    providers: [CommonModule],
    templateUrl: './annotation-tools.component.html',
    styleUrl: './annotation-tools.component.scss'
})
export class AnnotationToolsComponent implements OnInit, DoCheck {
    submitted: boolean = false;
    constructor(
        private destroyRef: DestroyRef,
        private lookupService: LookupService,
        private mapService: MapService,
        private spatialUtilService: SpatialUtilService,
        private mapToolsService: MapToolsService,
        private eventBus: NgEventBus,
        private widgetService: WidgetService,
        private mapModeService: MapModeService,
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private ngZone: NgZone
    ) {}

    title = 'AnnotationToolsController';
    widgetId = 'annotationTools';
    widgetConfig = {
        header: undefined,
        collapseTooltip: undefined,
        expandTooltip: undefined,
        closeTooltip: undefined
    };
    selectedAnnotationTool = {
        showInformation: false,
        information: [],
        errors: [],
        showErrors: false,
        title: '',
        showWarnings: false,
        warnings: [],
        id: undefined,
        type: undefined,
        errorMessage1: undefined,
        errorMessage2: undefined,
        stickyButton: false,
        finishButtonLabel: undefined,
        createButtonLabel: undefined,
        clearButtonLabel: undefined,
        closeButtonLabel: undefined,
        permissions: []
    };

    open = true; // Used to open and close accordion on UI

    toolRows = [{tools: []}];

    selectedGeneralAnnotationCount = 0;

    widgetVisibleAtLastCheck: boolean;

    editViewDesignPermissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    @ViewChild('widget')
    widgetElement: HTMLElement;

    @ViewChild('annotationToolsForm')
    form: NgForm;

    ngOnInit(): void {
        this.eventBus
            .on('toggleAnnotationToolsCreateButton')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - toggleAnnotationToolsCreateButton`);
            });

        this.eventBus
            .on('resetActionBehaviours')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - resetActionBehaviours`);
                this.resetActionBehaviours();
            });

        this.eventBus
            .on('resizeAnnotationWidget')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - resizeAnnotationWidget`);
                if (this.widgetElement) {
                    this.widgetService.setupWidget(this.widgetId, () => this.resizeAnnotationToolBody());
                }
            });
        this.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                auditTime(50), // throttle bursty stream
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.mapToolsService.isActive('ANNOTATION_TOOLS_EDIT')),
                tap(() => console.debug('on featureHighlightChanged with annotation')),
                tap(() => setTimeout(() => this.setupEditTool(), 50))
            )
            .subscribe();
        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find(l => l.name === this.widgetId);
        this.populateTools();
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId, () => this.resizeAnnotationToolBody());
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    private setFeatureLayersMouseEventsEnabled(enabled) {
        ['affectedlot', 'constructline', 'constructpoint', 'controlpoint', 'line', 'lot', 'polygon', 'road'].forEach(featureClass => {
            const featureLayer = this.spatialUtilService.getMapFeatureLayer(featureClass);
            if (enabled) {
                featureLayer.enableMouseEvents();
            } else {
                featureLayer.disableMouseEvents();
            }
        });
    }

    isWidgetActive() {
        return this.widgetService.isWidgetActive('AnnotationTools');
    }

    changeSelectedAnnotationTool(tool) {
        console.debug(this.title + ' : changeSelectedAnnotationTool');

        if (tool.type === 'construct') {
            if (this.mapToolsService.isActiveOrSuspended('ANNOTATION_TOOLS_EDIT')) {
                this.mapToolsService.deactivate('ANNOTATION_TOOLS_EDIT');
            }
        }

        if (this.isCreateActive()) {
            this.resetActionBehaviours();
        }

        this.selectedAnnotationTool = tool;

        this.eventBus.cast('clearError');

        this.setupEditTool();

        setTimeout(() => {
            this.resizeAnnotationToolBody();
        });
    }

    showAttribute() {
        return this.open && !(this.selectedAnnotationTool.type === 'edit' && this.selectedGeneralAnnotationCount !== 1);
    }

    setupEditTool() {
        console.debug(this.title + ' : setupEditTool');

        if (this.selectedAnnotationTool.type !== 'edit') {
            return;
        }

        this.mapToolsService
            .activate(
                'ANNOTATION_TOOLS_EDIT',
                () => {
                    const selectedAnnotationFeatures = this.spatialUtilService.getHighlightedAnnotations();

                    // Filter only general annotations
                    const selectedGeneralAnnotationFeatures = selectedAnnotationFeatures.filter(e => e.attributes.featuretype === 'general');
                    this.selectedGeneralAnnotationCount = selectedGeneralAnnotationFeatures.length;

                    if (selectedGeneralAnnotationFeatures.length === 0) {
                        if (this.selectedAnnotationTool && this.selectedAnnotationTool.type === 'edit') {
                            this.eventBus.cast('resetSelectedFeatures');
                            this.eventBus.cast('updateErrors', this.selectedAnnotationTool.errorMessage1);
                        }
                    } else {
                        if (this.areSelectedAnnotationsValid(selectedGeneralAnnotationFeatures)) {
                            if (this.selectedAnnotationTool && this.selectedAnnotationTool.type === 'edit') {
                                this.eventBus.cast('clearError');
                                this.eventBus.cast(this.selectedAnnotationTool.type + 'ReloadAnnotation', selectedGeneralAnnotationFeatures[0]);
                            }
                        } else {
                            if (this.selectedAnnotationTool && this.selectedAnnotationTool.type === 'edit') {
                                this.eventBus.cast('resetSelectedFeatures');
                                this.eventBus.cast('updateErrors', this.selectedAnnotationTool.errorMessage2);
                            }
                        }
                    }

                    setTimeout(() => {
                        this.resizeAnnotationToolBody();
                    });
                },
                () => {
                    if (this.selectedAnnotationTool && this.selectedAnnotationTool.stickyButton) {
                        console.info(`${this.title} - deactivating ANNOTATION_TOOLS_EDIT`);
                        this.eventBus.cast('editClearAnnotationTool');
                        this.mapToolsService.deactivate('ANNOTATION_TOOLS_EDIT');
                        this.resetActionBehaviours();
                        this.setFeatureLayersMouseEventsEnabled(true);
                    }
                }
            )
            .subscribe();
    }

    isCreateActive = () => this.mapToolsService.isActive('ANNOTATION_TOOLS_CREATE');

    areSelectedAnnotationsValid(selectedFeatures) {
        console.debug(this.title + ' : areSelectedAnnotationsValid');

        let output = true;

        // Check if selected annotations featureid has a different value than the first selected annotation
        if (selectedFeatures.some(e => e.attributes.featureid !== selectedFeatures[0].attributes.featureid)) {
            output = false;
        }

        if (selectedFeatures.length > 1) {
            output = false;
        }

        return output;
    }

    create() {
        console.debug(this.title + ' : create');

        this.submitted = true;
        this.form.control.markAllAsTouched();
        //this.form.$setSubmitted();

        if (this.form.invalid) {
            if (this.mapToolsService.isActive('ANNOTATION_TOOLS_CREATE')) {
                this.mapToolsService.deactivate('ANNOTATION_TOOLS_CREATE');
            }
            return;
        }

        if (this.selectedAnnotationTool.type === 'edit') {
            this.eventBus.cast('editCreateAnnotationTool');
            return;
        }

        if (this.mapToolsService.isActive('ANNOTATION_TOOLS_CREATE')) {
            this.mapToolsService.deactivate('ANNOTATION_TOOLS_CREATE');
        } else {
            this.mapToolsService
                .activate(
                    'ANNOTATION_TOOLS_CREATE',
                    () => {
                        if (this.selectedAnnotationTool.stickyButton) {
                            this.mapService.deleteMapClickHandler();
                        }
                        this.spatialUtilService.unhighlightAllGeometryFeatures();
                        this.eventBus.cast('constructCreateAnnotationTool');
                    },
                    () => {
                        console.info(`${this.title} - deactivating ANNOTATION_TOOLS_CREATE`);
                        this.mapService.deleteMapClickHandler();
                        this.eventBus.cast('constructFinishAnnotationTool');
                        this.resetActionBehaviours();
                    }
                )
                .subscribe();
        }
    }

    clear() {
        console.debug(this.title + ' : clear');
        this.eventBus.cast(this.selectedAnnotationTool.type + 'ClearAnnotationTool');
    }

    closeWidget() {
        console.debug(this.title + ' : closeWidget');

        this.resetActionBehaviours();
        this.mapToolsService.deactivate('ANNOTATION_TOOLS_WINDOW');
        this.setFeatureLayersMouseEventsEnabled(true);
    }

    isCreateDisabled() {
        return this.toolsDisabled() || (this.selectedAnnotationTool.type === 'edit' && this.form.invalid);
    }

    resetActionBehaviours() {
        console.debug(this.title + ' : resetActionBehaviours');
        this.mapService.resetMapClickHandler();
        this.mapService.disableSnappingTool();
        this.setFeatureLayersMouseEventsEnabled(false);
        this.mapService.disableMeasurementTool();
    }

    populateTools() {
        console.debug(this.title + ' : populateTools');

        this.lookupService.getLookups('annotationToolsItem').forEach(tool => {
            tool.errors = [];
            tool.showErrors = true;
            tool.warnings = [];
            tool.showWarnings = false;
            tool.information = [];
            tool.showInformation = false;

            tool.feature = {geometry: {}, attributes: {}};
            if (this.toolRows.length >= tool.row) {
                this.toolRows[tool.row - 1].tools.push(tool);
            } else {
                this.toolRows.push({tools: [tool]});
            }

            if (tool.defaultSelection) {
                this.selectedAnnotationTool = tool;
            }
        });
    }

    toolsDisabled = () => this.mapToolsService.isSuspended('ANNOTATION_TOOLS_WINDOW');

    // resizeAnnotationToolsBody(event, ui) {
    //     // console.debug(this.title + ' : resizeAnnotationToolsBody');
    //     //
    //     // const widget = $('#' + this.widgetId);
    //     // const annotationToolWidgets = widget.find('annotation-tool');
    //     //TODO ?????
    //     // annotationToolWidgets.forEach(annotationToolWidget => {
    //     //     this.resizeAnnotationToolBody(annotationToolWidget);
    //     // });
    // }

    resizeAnnotationToolBody() {
        console.debug(this.title + ' controller: resizeAnnotationToolBody');
        const widget = document.getElementById(this.widgetId);
        const annotationToolTab = document.getElementById(`${this.selectedAnnotationTool.type}AnnotationToolTab`);
        const widgetBody = annotationToolTab.querySelector('.annotation-tool-body');
        const widgetFooter = document.querySelector(`#${this.widgetId} div.inline-bottom-buttons`);

        if (widget && widgetBody && (widgetBody as HTMLElement).offsetTop && widgetFooter && (widgetFooter as HTMLElement).offsetTop) {
            const widgetBodyTop = (widgetBody as HTMLElement).offsetTop;
            const widgetBodyHeight = (widgetFooter as HTMLElement).offsetTop - widgetBodyTop;
            (widgetBody as HTMLElement).style.height = `${widgetBodyHeight}`;
        }
    }

    getHelpUrl() {
        const toolTypeKebabCase = this.selectedAnnotationTool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `annotation-tools-${toolTypeKebabCase}-help.html`;
    }

    compareOrder = (a: any, b: any) => a.order - b.order;
    getHelpPageTitle() {
        const toolDescription = {
            construct: 'Create an Annotation',
            edit: 'Edit an Annotation'
        }[this.selectedAnnotationTool.type];

        return `Annotation Tools | ${toolDescription}`;
    }

    showOrHide() {
        this.widgetService.showOrHideWidgetContent('annotationTools', this.open);
        this.open = !this.open;
    }

    isWidgetDisabled() {
        return this.widgetService.isWidgetDisabled('AnnotationTools', this.mapModeService.mapMode) || this.annotationToolsDisabled();
    }

    annotationToolsDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewDesignPermissions);
    }

    getTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ANNOTATION_TOOLS_WINDOW', 'Annotation Tools');
    }

    onClick() {
        this.widgetService.onClick('AnnotationTools');
    }

    isDisabledByPermissions() {
        return this.permissionsService.isDisabledByPermissions(this.selectedAnnotationTool.permissions);
    }
}
